import { useTranslate } from '@/hooks/useTranslate';
import { cn } from '@/utils/cn';
import { logInfo } from '@/utils/logger';
import { InfoLogs } from '@/constants/logs';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useContext } from 'react';
import { useCancelOrder } from '@/api/order';
import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';
import { mixpanelService } from '@/services/mixpanelService';
import { notifyCloseCheckout } from '@/utils/notifier';
import { useCheckoutData } from '@/api/useCheckoutData';
import { getEventDataForSdkEvents } from '@/utils/eventDataBuilder';
import { Button } from '../Common/Button';
import { ButtonType } from '@/types/components';
import { generateZendeskSupportURL } from '@/utils/support';
import { FontAwesomeIcon } from '@/components/Common/FontAwesomeIcon/FontAwesomeIcon';
import type { ICheckoutContext } from '@/types/context';
import type { FallbackProps } from 'react-error-boundary';
import File from '@/assets/icons/common/file.svg';
import { isLandscape } from '@/utils/device';
import { Footer } from '../Footer/Footer';
import { getCheckoutSolution } from '@/utils/checkoutSolution';
import { checkoutErrorMessageKey, getDescriptionForErrorMessage } from '@/utils/errorScreen';
import { CheckoutRedirectOrderStatus } from '@/constants/checkoutRedirectOrderStatus';
import { useRedirectLink } from '@/hooks/useRedirectLink';
import { redirectUser } from '@/utils/location';
import { Modal } from '../Common/Modal';

export function ErrorScreenFallbackRender({ error }: FallbackProps) {
  const context = useContext(CheckoutContext);
  // Fallback renders outside CheckoutContext.Provider when the boundary catches
  // errors under the provider, so nested context fields may be missing.
  const checkoutConfiguration = context.checkoutConfiguration;
  const checkoutData = useCheckoutData({ enableFetch: false });
  const { t } = useTranslate();
  const { mutateAsync: cancelOrder } = useCancelOrder();
  const errorMessage = checkoutErrorMessageKey(error);
  const { isPaymentLinks } = getCheckoutSolution(checkoutConfiguration);
  const { fullyRedirectLink } = useRedirectLink(CheckoutRedirectOrderStatus.FAILED);

  const handleClose = async () => {
    logInfo(InfoLogs.checkoutClosed);
    const properties = getMixpanelProperties(context as ICheckoutContext);
    mixpanelService.track(MixpanelEvents.CHECKOUT_CLOSED, properties as Record<string, unknown>);
    await cancelOrder().finally(() => {
      notifyCloseCheckout(
        getEventDataForSdkEvents(checkoutData.data ?? {}, { error: { reason: t(errorMessage), isFatal: true } })
      );
    });
    if (isPaymentLinks && fullyRedirectLink) {
      redirectUser(fullyRedirectLink ?? '');
    }
  };

  const openSupport = () => {
    window.open(
      generateZendeskSupportURL({
        customerId: context.customer?.id,
        orderId: '',
        publisherId: context.publisherId,
        customerCountryCode: context.customer?.geoLocator?.countryCode2,
      }),
      '_blank'
    );
  };

  return (
    <Modal>
      <div className="absolute inset-0 z-[200] flex h-full flex-col overflow-y-auto bg-white">
        <div className="relative h-5 w-full shrink-0 md:h-0">
          <button
            type="button"
            onClick={handleClose}
            className={cn(
              'absolute right-2 top-2 z-50 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full',
              'bg-white text-base-black transition-all duration-200 md:right-6 md:top-6'
            )}
            aria-label={t('closeCheckout')}
          >
            <FontAwesomeIcon icon="fa-solid fa-xmark" className="text-lg" />
          </button>
        </div>

        <div
          className={cn('flex w-full flex-1 flex-col items-center justify-center gap-6 px-6 pb-8 pt-2 landscape:pt-10')}
        >
          <img src={File} alt="File" className="w-40 h-40" />
          <div className="flex max-w-md flex-col items-center justify-center gap-1 text-center">
            <h1 className="family-Inter text-xl font-bold text-card-foreground">{t(errorMessage)}</h1>
            <p className="family-Inter text-base font-normal text-muted-foreground">
              {t(getDescriptionForErrorMessage(errorMessage))}
            </p>
          </div>
          <div className="flex w-full max-w-[380px] flex-col items-center justify-center gap-2">
            <Button type={ButtonType.BLUE} onClick={handleClose} className="w-full max-w-[340px]">
              {isPaymentLinks ? t('backToGame') : t('backToStore')}
            </Button>

            <Button type={ButtonType.LIGHT} onClick={openSupport} className="w-full max-w-[340px]">
              {t('contactSupport')}
            </Button>
          </div>
        </div>
        <div className={cn('w-full shrink-0 ', !isLandscape() && 'md:sticky md:bottom-0 md:z-10 md:bg-white')}>
          <Footer />
        </div>
      </div>
    </Modal>
  );
}
